/**
 * Custom Overlay Registrations for KLineChart
 * Includes Planet Line and Trend Line overlays with smooth curve rendering
 * @version 1.1.0 - Fixed registration and removed alert
 */

import { registerOverlay, registerFigure } from 'klinecharts';
import moment from "moment";

// ============================================
// Catmull-Rom Spline Interpolation
// ============================================

function catmullRomSpline(points, numInterpolated = 10) {
  if (points.length < 2) return points;
  if (points.length === 2) {
    const result = [];
    for (let t = 0; t <= numInterpolated; t++) {
      const ratio = t / numInterpolated;
      result.push({
        x: points[0].x + (points[1].x - points[0].x) * ratio,
        y: points[0].y + (points[1].y - points[0].y) * ratio,
      });
    }
    return result;
  }

  const result = [];

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(i + 2, points.length - 1)];

    for (let t = 0; t < numInterpolated; t++) {
      const tNorm = t / numInterpolated;
      const t2 = tNorm * tNorm;
      const t3 = t2 * tNorm;

      const x =
        0.5 *
        (2 * p1.x +
          (-p0.x + p2.x) * tNorm +
          (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
          (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);

      const y =
        0.5 *
        (2 * p1.y +
          (-p0.y + p2.y) * tNorm +
          (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
          (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3);

      result.push({ x, y });
    }
  }

  result.push({
    x: points[points.length - 1].x,
    y: points[points.length - 1].y,
  });

  return result;
}

// ============================================
// Planet Line Overlay (Figure-based)
// ============================================

export function registerPlanetLineOverlay() {
  registerOverlay({
    name: 'planetLine',
    totalStep: 0,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,

    createPointFigures: ({ overlay, coordinates }) => {
      if (!coordinates || coordinates.length < 2) {
        return [];
      }

      const { extendData } = overlay;
      const {
        lineColor = '#1677FF',
        lineWidth = 1,
        lineStyle = 0,
        smooth = true,
        interpolationPoints = 8,
      } = extendData || {};

      let drawCoords = coordinates;
      if (smooth && coordinates.length >= 3) {
        drawCoords = catmullRomSpline(
          coordinates.map((c) => ({ x: c.x, y: c.y })),
          interpolationPoints
        );
      }

      const lineCoords = drawCoords.map((coord) => ({
        x: coord.x,
        y: coord.y,
      }));

      return [
        {
          type: 'line',
          attrs: { coordinates: lineCoords },
          styles: {
            style: lineStyle === 0 ? 'solid' : 'dashed',
            color: lineColor,
            size: lineWidth,
            dashedValue: lineStyle === 1 ? [4, 4] : [2, 2],
          },
        },
      ];
    },
  });
}

// ============================================
// Planet Line Canvas Overlay (Custom Draw)
// ============================================

export function registerPlanetLineCanvasOverlay() {
  registerOverlay({
    name: 'planetLineCanvas',
    totalStep: 0,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,

    draw: ({ ctx, overlay, coordinates, bounding }) => {
      if (!coordinates || coordinates.length < 2 || !ctx) {
        return false;
      }

      const { extendData } = overlay;
      const {
        lineColor = '#1677FF',
        lineWidth = 1,
        lineStyle = 0,
        smooth = true,
        tension = 0.4,
      } = extendData || {};

      ctx.save();
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (lineStyle === 1) {
        ctx.setLineDash([4, 4]);
      } else {
        ctx.setLineDash([]);
      }

      ctx.beginPath();
      ctx.moveTo(coordinates[0].x, coordinates[0].y);

      if (smooth && coordinates.length >= 3) {
        for (let i = 0; i < coordinates.length - 1; i++) {
          const curr = coordinates[i];
          const next = coordinates[i + 1];

          let cp1x, cp1y, cp2x, cp2y;

          if (i === 0) {
            const nextNext = coordinates[Math.min(i + 2, coordinates.length - 1)];
            cp1x = curr.x + (next.x - curr.x) * tension;
            cp1y = curr.y + (next.y - curr.y) * tension;
            cp2x = next.x - (nextNext.x - curr.x) * tension;
            cp2y = next.y - (nextNext.y - curr.y) * tension;
          } else if (i === coordinates.length - 2) {
            const prev = coordinates[i - 1];
            cp1x = curr.x + (next.x - prev.x) * tension;
            cp1y = curr.y + (next.y - prev.y) * tension;
            cp2x = next.x - (next.x - curr.x) * tension;
            cp2y = next.y - (next.y - curr.y) * tension;
          } else {
            const prev = coordinates[i - 1];
            const nextNext = coordinates[i + 2];
            cp1x = curr.x + (next.x - prev.x) * tension;
            cp1y = curr.y + (next.y - prev.y) * tension;
            cp2x = next.x - (nextNext.x - curr.x) * tension;
            cp2y = next.y - (nextNext.y - curr.y) * tension;
          }

          ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, next.x, next.y);
        }
      } else {
        for (let i = 1; i < coordinates.length; i++) {
          ctx.lineTo(coordinates[i].x, coordinates[i].y);
        }
      }

      ctx.stroke();
      ctx.restore();

      return true;
    },
  });
}

// ============================================
// Trend Line Overlay
// ============================================

export function registerPredictionLineOverlay() {
  registerOverlay({
    name: 'predictionLine',
    totalStep: 0,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,

    createPointFigures: ({ overlay, coordinates }) => {
      if (!coordinates || coordinates.length < 2) {
        return [];
      }

      const { extendData = {} } = overlay;
      const {
        lineColor = '#2962FF',
        lineWidth = 2.5,
        showMarkers = false,
        markerRadius = 5,
        markerColor,
      } = extendData;

      const figures = [];

      // Add the line
      figures.push({
        type: 'line',
        attrs: { 
          coordinates: coordinates 
        },
        styles: {
          style: 'solid',
          color: lineColor,
          size: lineWidth,
        },
      });

      // Add markers at each point if enabled
      // if (showMarkers) {
      //   const color = markerColor || lineColor;
      //   coordinates.forEach((coord) => {
      //     figures.push({
      //       type: 'circle',
      //       attrs: {
      //         x: coord.x,
      //         y: coord.y,
      //         r: markerRadius,
      //       },
      //       styles: {
      //         style: 'fill',           // Fill the circle
      //         color: color,            // Fill color
      //         borderStyle: 'solid',    // Border style
      //         borderColor: color,      // Border color
      //         borderSize: 1,           // Border width
      //       },
      //     });
      //   });
      // }

      return figures;
    },
  });
}

export function registerPredictionMarkersOverlay() {
  registerOverlay({
    name: 'predictionMarkers',
    totalStep: 0,
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,

    createPointFigures: ({ overlay, coordinates }) => {
      if (!coordinates || coordinates.length < 1) return [];

      const { extendData = {} } = overlay;
      const {
        markerColor = '#2962FF',
        markerRadius = 4,
      } = extendData;

      return coordinates.map((coord) => ({
        type: 'circle',
        attrs: { x: coord.x, y: coord.y, r: markerRadius },
        styles: {
          style: 'fill',
          color: markerColor,
          borderStyle: 'solid',
          borderColor: markerColor,
          borderSize: 1,
        },
      }));
    },
  });
}

/**
 * Price Range Overlay for KLineChart
 * "Price Range" / "Percentage" measurement tool
 * 
 * User clicks two points on the chart. The overlay draws:
 *   - A shaded rectangle between the two price levels
 *   - Horizontal dashed lines at both price levels
 *   - A vertical connector line
 *   - A label showing: price difference, percentage change, and bar count
 * 
 * @version 1.0.0
 */

// ============================================
// Color constants matching TradingView
// ============================================
const COLORS = {
  main: '#2962FF',
  rectFill: 'rgba(41, 98, 255, 0.08)',
  rectBorder: 'rgba(41, 98, 255, 0.4)',
  dashLine: 'rgba(41, 98, 255, 0.6)',
  labelBg: 'rgba(255, 255, 255, 1)',
  labelText: '#000000',
  endpointFill: '#FFFFFF',
  endpointBorder: '#2962FF',
};


// ============================================
// STEP 1: Register a custom figure that draws
//         a centered label with canvas
// ============================================

let figureRegistered = false;

export function registerPriceRangeLabelFigure() {
  if (figureRegistered) return;

  registerFigure({
    name: 'priceRangeLabel',

    draw: (ctx, attrs, styles) => {
      const { x, y, text } = attrs;
      const {
        backgroundColor = 'rgba(255, 255, 255, 1)',
        color = '#000',
        size = 11,
        family = '-apple-system, BlinkMacSystemFont, Helvetica Neue, Arial, sans-serif',
        weight = '600',
        paddingH = 8,
        paddingV = 5,
        borderRadius = 3,
      } = styles || {};

      if (!text) return;

      ctx.save();

      // Measure text
      const font = `${weight} ${size}px ${family}`;
      ctx.font = font;
      const textMetrics = ctx.measureText(text);
      const textW = textMetrics.width;

      const lw = textW + paddingH * 2;
      const lh = size + paddingV * 2;
      const lx = x - lw / 2;   // x is center point
      const ly = y;             // y is top of label
      const r = borderRadius;

      // Draw rounded rect background
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 2;
      ctx.fillStyle = backgroundColor;
      ctx.beginPath();
      ctx.moveTo(lx + r, ly);
      ctx.lineTo(lx + lw - r, ly);
      ctx.quadraticCurveTo(lx + lw, ly, lx + lw, ly + r);
      ctx.lineTo(lx + lw, ly + lh - r);
      ctx.quadraticCurveTo(lx + lw, ly + lh, lx + lw - r, ly + lh);
      ctx.lineTo(lx + r, ly + lh);
      ctx.quadraticCurveTo(lx, ly + lh, lx, ly + lh - r);
      ctx.lineTo(lx, ly + r);
      ctx.quadraticCurveTo(lx, ly, lx + r, ly);
      ctx.closePath();
      ctx.fill();

      // Reset shadow before drawing text (otherwise text gets shadow too)
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Draw centered text
      ctx.fillStyle = color;
      ctx.font = font;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x, ly + lh / 2);

      ctx.restore();
    },

    checkEventOn: (coordinate, attrs, styles) => {
      // Basic hit test for the label area
      const { x, y, text } = attrs;
      const { size = 11, paddingH = 8, paddingV = 5 } = styles || {};
      if (!text) return false;

      const estW = text.length * 7;
      const lw = estW + paddingH * 2;
      const lh = size + paddingV * 2;
      const lx = x - lw / 2;
      const ly = y;

      return (
        coordinate.x >= lx &&
        coordinate.x <= lx + lw &&
        coordinate.y >= ly &&
        coordinate.y <= ly + lh
      );
    },
  });

  figureRegistered = true;
}


// ============================================
// STEP 2: Register the Price Range overlay
// ============================================

export function registerPriceRangeOverlay() {
  // Ensure custom figure is registered first
  registerPriceRangeLabelFigure();

  registerOverlay({
    name: 'priceRange',
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,

    createPointFigures: ({ overlay, coordinates, bounding }) => {
      if (!coordinates || coordinates.length < 2) return [];

      const figures = [];
      const points = overlay.points || [];
      const c1 = coordinates[0];
      const c2 = coordinates[1];

      // Data
      const price1 = points[0]?.value ?? 0;
      const price2 = points[1]?.value ?? 0;
      const priceDiff = price2 - price1;
      const pctChange = price1 !== 0 ? (priceDiff / price1) * 100 : 0;
      const isUp = priceDiff >= 0;

      const ts1 = points[0]?.timestamp ?? 0;
      const ts2 = points[1]?.timestamp ?? 0;
      const barCount = Math.abs(Math.round((ts2 - ts1) / 86400000));

      // Geometry
      const left   = Math.min(c1.x, c2.x);
      const right  = Math.max(c1.x, c2.x);
      const top    = Math.min(c1.y, c2.y);
      const bottom = Math.max(c1.y, c2.y);
      const w      = right - left;
      const h      = bottom - top;
      const midX   = (c1.x + c2.x) / 2;
      const chartW = bounding?.width || 9999;

      // ── 1. Shaded rectangle ──
      if (w > 0 && h > 0) {
        figures.push({
          type: 'rect',
          attrs: { x: left, y: top, width: w, height: h },
          styles: {
            style: 'stroke_fill',
            color: COLORS.rectFill,
            borderStyle: 'solid',
            borderColor: COLORS.rectBorder,
            borderSize: 1,
          },
        });
      }

      // ── 2. Horizontal dashed lines ──
      // [c1.y, c2.y].forEach(y => {
      //   figures.push({
      //     type: 'line',
      //     attrs: { coordinates: [{ x: 0, y }, { x: chartW, y }] },
      //     styles: { style: 'dashed', color: COLORS.dashLine, size: 1, dashedValue: [6, 4] },
      //   });
      // });

      // ── 3. Vertical connector ──
      figures.push({
        type: 'line',
        attrs: { coordinates: [{ x: midX, y: c1.y }, { x: midX, y: c2.y }] },
        styles: { style: 'solid', color: COLORS.main, size: 1 },
      });

      // ── 4. Arrow heads (line pairs) ──
      const aw = 4;
      const ah = 7;
      // Top arrow
      figures.push({
        type: 'line',
        attrs: { coordinates: [{ x: midX - aw, y: top + ah }, { x: midX, y: top }] },
        styles: { style: 'solid', color: COLORS.main, size: 1.5 },
      });
      figures.push({
        type: 'line',
        attrs: { coordinates: [{ x: midX + aw, y: top + ah }, { x: midX, y: top }] },
        styles: { style: 'solid', color: COLORS.main, size: 1.5 },
      });
      // Bottom arrow
      figures.push({
        type: 'line',
        attrs: { coordinates: [{ x: midX - aw, y: bottom - ah }, { x: midX, y: bottom }] },
        styles: { style: 'solid', color: COLORS.main, size: 1.5 },
      });
      figures.push({
        type: 'line',
        attrs: { coordinates: [{ x: midX + aw, y: bottom - ah }, { x: midX, y: bottom }] },
        styles: { style: 'solid', color: COLORS.main, size: 1.5 },
      });

      // ── 5. Info label (custom figure for perfect centering) ──
      const sign = isUp ? '+' : '';
      const labelText = `${sign}${priceDiff.toFixed(2)} (${sign}${pctChange.toFixed(2)}%) ${barCount.toLocaleString()}`;

      figures.push({
        type: 'priceRangeLabel',
        attrs: {
          x: midX,          // center X
          y: bottom + 6,    // top Y of label
          text: labelText,
        },
        styles: {
          backgroundColor: COLORS.labelBg,
          color: COLORS.labelText,
          size: 11,
          weight: '600',
          family: '-apple-system, BlinkMacSystemFont, Helvetica Neue, Arial, sans-serif',
          paddingH: 8,
          paddingV: 5,
          borderRadius: 3,
        },
      });

      // ── 6. Endpoint circles ──
      [c1, c2].forEach(c => {
        figures.push({
          type: 'circle',
          attrs: { x: c.x, y: c.y, r: 4.5 },
          styles: {
            style: 'stroke_fill',
            color: COLORS.endpointFill,
            borderStyle: 'solid',
            borderColor: COLORS.endpointBorder,
            borderSize: 1.5,
          },
        });
      });

      return figures;
    },

    // ── Y-Axis price labels ──
    // createYAxisFigures: ({ overlay, coordinates, bounding, precision }) => {
    //   if (!coordinates || coordinates.length < 2) return [];

    //   const points = overlay.points || [];
    //   const yAxisW = bounding?.width || 60;
    //   const prec = precision?.price ?? 2;

    //   return [
    //     { y: coordinates[0].y, price: points[0]?.value ?? 0 },
    //     { y: coordinates[1].y, price: points[1]?.value ?? 0 },
    //   ].map(({ y, price }) => ({
    //     type: 'text',
    //     attrs: {
    //       x: 0, y: y - 10,
    //       width: yAxisW, height: 20,
    //       text: price.toFixed(prec),
    //       align: 'center',
    //       baseline: 'middle',
    //     },
    //     styles: {
    //       style: 'fill',
    //       color: '#FFFFFF',
    //       size: 11,
    //       family: '-apple-system, BlinkMacSystemFont, Helvetica Neue, Arial, sans-serif',
    //       weight: 'normal',
    //       paddingLeft: 4, paddingRight: 4,
    //       paddingTop: 3, paddingBottom: 3,
    //       borderRadius: 2,
    //       backgroundColor: COLORS.main,
    //     },
    //   }));
    // },

    // ── X-Axis date labels ──
    // createXAxisFigures: ({ overlay, coordinates, bounding }) => {
    //   if (!coordinates || coordinates.length < 2) return [];

    //   const points = overlay.points || [];
    //   const xAxisH = bounding?.height || 30;

    //   return [
    //     { x: coordinates[0].x, ts: points[0]?.timestamp },
    //     { x: coordinates[1].x, ts: points[1]?.timestamp },
    //   ]
    //     .filter(({ ts }) => ts)
    //     .map(({ x, ts }) => ({
    //       type: 'text',
    //       attrs: {
    //         x: x - 55, y: 0,
    //         width: 110, height: xAxisH,
    //         text: moment(ts).format('ddd D MMM \'YY'),
    //         align: 'center',
    //         baseline: 'middle',
    //       },
    //       styles: {
    //         style: 'fill',
    //         color: '#FFFFFF',
    //         size: 11,
    //         family: '-apple-system, BlinkMacSystemFont, Helvetica Neue, Arial, sans-serif',
    //         weight: 'normal',
    //         paddingLeft: 6, paddingRight: 6,
    //         paddingTop: 3, paddingBottom: 3,
    //         borderRadius: 2,
    //         backgroundColor: COLORS.main,
    //       },
    //     }));
    // },
  });
}


/**
 * Register a simplified "draw" based version that uses canvas directly.
 * This gives more control over rendering (e.g., proper text measurement).
 */
export function registerPriceRangeCanvasOverlay() {
  registerOverlay({
    name: 'priceRangeCanvas',
    
    totalStep: 3,
    
    needDefaultPointFigure: false,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,

    draw: ({ ctx, overlay, coordinates, bounding }) => {
      if (!ctx || !coordinates || coordinates.length < 2) return false;

      const points = overlay.points || [];
      const coord1 = coordinates[0];
      const coord2 = coordinates[1];
      
      const price1 = points[0]?.value ?? 0;
      const price2 = points[1]?.value ?? 0;
      
      const priceDiff = price2 - price1;
      const percentChange = price1 !== 0 ? ((priceDiff / price1) * 100) : 0;
      const isPositive = priceDiff >= 0;
      
      const ts1 = points[0]?.timestamp ?? 0;
      const ts2 = points[1]?.timestamp ?? 0;
      const barCount = Math.abs(Math.round((ts2 - ts1) / (24 * 60 * 60 * 1000)));
      
      // Colors
      const mainColor = isPositive ? '#2DC08E' : '#F92855';
      const bgColor = isPositive ? 'rgba(45, 192, 142, 0.12)' : 'rgba(249, 40, 85, 0.12)';

      const left = Math.min(coord1.x, coord2.x);
      const right = Math.max(coord1.x, coord2.x);
      const top = Math.min(coord1.y, coord2.y);
      const bottom = Math.max(coord1.y, coord2.y);
      const width = right - left;
      const height = bottom - top;
      const midX = (coord1.x + coord2.x) / 2;
      const midY = (coord1.y + coord2.y) / 2;
      const chartWidth = bounding?.width || 9999;

      ctx.save();

      // --- 1. Shaded rectangle ---
      if (width > 0 && height > 0) {
        ctx.fillStyle = bgColor;
        ctx.fillRect(left, top, width, height);
      }

      // --- 2. Horizontal dashed lines ---
      ctx.strokeStyle = mainColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      
      // Line at price1
      ctx.beginPath();
      ctx.moveTo(0, coord1.y);
      ctx.lineTo(chartWidth, coord1.y);
      ctx.stroke();
      
      // Line at price2
      ctx.beginPath();
      ctx.moveTo(0, coord2.y);
      ctx.lineTo(chartWidth, coord2.y);
      ctx.stroke();

      // --- 3. Vertical connector ---
      ctx.setLineDash([]);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(midX, coord1.y);
      ctx.lineTo(midX, coord2.y);
      ctx.stroke();

      // --- 4. Arrow heads ---
      const arrowSize = 5;
      const topY = Math.min(coord1.y, coord2.y);
      const bottomY = Math.max(coord1.y, coord2.y);
      
      ctx.fillStyle = mainColor;
      // Top arrow
      ctx.beginPath();
      ctx.moveTo(midX, topY);
      ctx.lineTo(midX - arrowSize, topY + arrowSize * 2);
      ctx.lineTo(midX + arrowSize, topY + arrowSize * 2);
      ctx.closePath();
      ctx.fill();
      // Bottom arrow
      ctx.beginPath();
      ctx.moveTo(midX, bottomY);
      ctx.lineTo(midX - arrowSize, bottomY - arrowSize * 2);
      ctx.lineTo(midX + arrowSize, bottomY - arrowSize * 2);
      ctx.closePath();
      ctx.fill();

      // --- 5. Info label ---
      const sign = isPositive ? '+' : '';
      const labelLine1 = `${sign}${priceDiff.toFixed(2)}  (${sign}${percentChange.toFixed(2)}%)`;
      const labelLine2 = `${barCount} bars`;
      
      const fontSize = 12;
      const fontBold = `bold ${fontSize}px Helvetica Neue, Arial, sans-serif`;
      const fontNormal = `${fontSize}px Helvetica Neue, Arial, sans-serif`;
      
      ctx.font = fontBold;
      const line1Width = ctx.measureText(labelLine1).width;
      ctx.font = fontNormal;
      const line2Width = ctx.measureText(labelLine2).width;
      
      const maxTextWidth = Math.max(line1Width, line2Width);
      const labelPadH = 12;
      const labelPadV = 6;
      const lineSpacing = 4;
      const labelW = maxTextWidth + labelPadH * 2;
      const labelH = fontSize * 2 + lineSpacing + labelPadV * 2;
      const labelX = midX - labelW / 2;
      const labelY = midY - labelH / 2;

      // Label background with rounded corners
      const radius = 5;
      ctx.fillStyle = mainColor;
      ctx.beginPath();
      ctx.moveTo(labelX + radius, labelY);
      ctx.lineTo(labelX + labelW - radius, labelY);
      ctx.quadraticCurveTo(labelX + labelW, labelY, labelX + labelW, labelY + radius);
      ctx.lineTo(labelX + labelW, labelY + labelH - radius);
      ctx.quadraticCurveTo(labelX + labelW, labelY + labelH, labelX + labelW - radius, labelY + labelH);
      ctx.lineTo(labelX + radius, labelY + labelH);
      ctx.quadraticCurveTo(labelX, labelY + labelH, labelX, labelY + labelH - radius);
      ctx.lineTo(labelX, labelY + radius);
      ctx.quadraticCurveTo(labelX, labelY, labelX + radius, labelY);
      ctx.closePath();
      ctx.fill();

      // Label text
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      ctx.font = fontBold;
      ctx.fillText(labelLine1, midX, labelY + labelPadV + fontSize / 2);
      
      ctx.font = fontNormal;
      ctx.fillText(labelLine2, midX, labelY + labelPadV + fontSize + lineSpacing + fontSize / 2);

      // --- 6. Endpoint dots ---
      [coord1, coord2].forEach((coord) => {
        ctx.fillStyle = mainColor;
        ctx.beginPath();
        ctx.arc(coord.x, coord.y, 5, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(coord.x, coord.y, 5, 0, Math.PI * 2);
        ctx.stroke();
      });

      ctx.restore();
      return true;
    },

    // Y-axis labels using canvas
    createYAxisFigures: ({ overlay, coordinates, bounding, precision }) => {
      if (!coordinates || coordinates.length < 2) return [];
      
      const points = overlay.points || [];
      const price1 = points[0]?.value ?? 0;
      const price2 = points[1]?.value ?? 0;
      const priceDiff = price2 - price1;
      const isPositive = priceDiff >= 0;
      const mainColor = isPositive ? '#2DC08E' : '#F92855';
      const yAxisWidth = bounding?.width || 60;
      const pricePrecision = precision?.price ?? 2;

      return [
        { y: coordinates[0].y, price: price1 },
        { y: coordinates[1].y, price: price2 },
      ].map(({ y, price }) => ({
        type: 'text',
        attrs: {
          x: 0,
          y: y - 10,
          width: yAxisWidth,
          height: 20,
          text: price.toFixed(pricePrecision),
          align: 'center',
          baseline: 'middle',
        },
        styles: {
          style: 'fill',
          color: '#FFFFFF',
          size: 11,
          family: 'Helvetica Neue, Arial, sans-serif',
          weight: 'normal',
          paddingLeft: 4,
          paddingRight: 4,
          paddingTop: 3,
          paddingBottom: 3,
          borderRadius: 2,
          backgroundColor: mainColor,
        },
      }));
    },
  });
}

// ============================================
// Initialize All Custom Overlays
// ============================================

let overlaysRegistered = false;

export function registerAllOverlays() {
  if (overlaysRegistered) {
    console.log('KLineChart overlays already registered');
    return;
  }

  try {
    registerPlanetLineOverlay();
    // registerPlanetLineCanvasOverlay();
    // registerTrendLineOverlay();
    registerPredictionLineOverlay();
    registerPredictionMarkersOverlay();
    registerPriceRangeOverlay();
    // registerPredictionLineOverlay();
    overlaysRegistered = true;
    console.log('KLineChart custom overlays registered successfully');
  } catch (error) {
    console.error('Failed to register custom overlays:', error);
  }
}

export function areOverlaysRegistered() {
  return overlaysRegistered;
}